import { useEffect, useRef, useState, useLayoutEffect } from "react";
import { Button } from "../../../../core/components/atoms/Button";
import './styles.css';
import { ImageContent, ImageWrapper } from "./variants";
import "tippy.js/dist/tippy.css";
import tippy from "tippy.js";
import { useEssayAuxiliarStore } from "../../stores/essayAuxiliarStore";

interface IComment {
  id: number;
  top: number;
  left: number;
  height: number;
  width: number;
}

interface ICorrectionImage {
  imageUrl: string;
  imageMap: string;
  rotation?: number;
  comments?: IComment[] | string;
}

export function CorrectionImage({ imageUrl, imageMap, rotation, comments }: ICorrectionImage) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [rotate, setRotate] = useState<number>(0);
  const [processedImageMap, setProcessedImageMap] = useState<string>(imageMap);
  
  const { setSelected } = useEssayAuxiliarStore();

  useLayoutEffect(() => {
    if (wrapperRef.current) {
      setDimensions({
        width: wrapperRef.current.clientWidth,
        height: wrapperRef.current.clientHeight
      });
    }
  }, [rotate]);

  useEffect(() => {
    if(rotation && rotation > 0) {
      setRotate(rotation);
    }
  }, [rotation])

  useEffect(() => {
    if (comments && imageMap) {
      let processedMap = imageMap;
      const commentsArray = typeof comments === 'string' ? JSON.parse(comments) : comments;
      
      if (Array.isArray(commentsArray) && commentsArray.length > 0) {
        // Parse the imageMap to add data-id attributes
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${imageMap}</div>`, 'text/html');
        const spans = doc.querySelectorAll('.mark');
        
        spans.forEach(span => {
          const htmlSpan = span as HTMLElement;
          const spanTop = parseFloat(htmlSpan.style.top);
          const spanLeft = parseFloat(htmlSpan.style.left);
          const spanHeight = parseFloat(htmlSpan.style.height);
          const spanWidth = parseFloat(htmlSpan.style.width);
          
          // Find matching comment based on position values
          const matchingComment = commentsArray.find(comment => {
            const topDiff = Math.abs(comment.top - spanTop);
            const leftDiff = Math.abs(comment.left - spanLeft);
            const heightDiff = Math.abs(comment.height - spanHeight);
            const widthDiff = Math.abs(comment.width - spanWidth);
            
            // Allow small tolerance for floating point differences
            return topDiff < 0.1 && leftDiff < 0.1 && heightDiff < 0.1 && widthDiff < 0.1;
          });
          
          if (matchingComment) {
            span.setAttribute('data-id', matchingComment.id.toString());
            // Add class to make spans clickable
            span.classList.add('clickable-mark');
          }
        });
        
        // Convert back to string
        processedMap = doc.querySelector('div')?.innerHTML || imageMap;
      }
      
      setProcessedImageMap(processedMap);
    } else {
      setProcessedImageMap(imageMap);
    }
  }, [imageMap, comments]);

  useEffect(() => {
    tippy("[data-content]", { 
      content: (reference) => reference.getAttribute("data-content") as string | Element
    });
  }, [processedImageMap]);

  // Add click event listener for spans
  useEffect(() => {
    const handleSpanClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('mark') && target.hasAttribute('data-id')) {
        const dataId = target.getAttribute('data-id');
        const commentId = dataId ? parseInt(dataId, 10) : null;
        setSelected(commentId);
      }
    };

    document.addEventListener('click', handleSpanClick);

    return () => {
      document.removeEventListener('click', handleSpanClick);
    };
  }, [setSelected]);

  const rotateImage = () => {
    let newRotation = rotate + 90;
    
    if (newRotation >= 360) {
      newRotation = 0;
    }

    setRotate(newRotation);
  }

  return (
    <div className="flex flex-col gap-md">
      <Button LeftIcon="refresh" iconType="material" classNameIcon="text-md" className="w-fit" onClick={() => rotateImage()}>
        Girar imagem
      </Button>
      <div className={ImageWrapper()} ref={wrapperRef}>
        <img 
          className={ImageContent()}
          style={{ 
            transform: `rotate(${rotate}deg)`, 
            transformOrigin: 'center',
            width: rotate === 90 || rotate === 270 ? dimensions.height : '100%',
            height: rotate === 90 || rotate === 270 ? dimensions.width : 'auto'
          }}
          src={imageUrl} 
          alt="image-essay"
          useMap="#points"
          title="redacao"
        />
        <map 
          name="points"
          className="z-10" 
          id="image-map" 
          dangerouslySetInnerHTML={{
            __html: processedImageMap
          }}
        />
      </div>
    </div>
  )
}